export const MAX_DOWNLOAD_ATTEMPTS = 3;

export const isTransientDownloadError = (error) => {
  const status = error?.response?.status;
  const code = error?.code;
  return status === 502 || status === 503 || status === 504 || code === 'ERR_NETWORK';
};

// Shared bounded-retry wrapper: `downloadFn(onProgress)` is retried on a
// transient (502/503/504/network) error, up to maxAttempts, the same
// resilience the tray's manual download and the pre-migration Karma export
// both had. Throws the last error if every attempt fails.
export const downloadWithRetry = async (downloadFn, { onProgress, maxAttempts = MAX_DOWNLOAD_ATTEMPTS } = {}) => {
  let lastError;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await downloadFn(onProgress);
    } catch (error) {
      lastError = error;
      if (attempt >= maxAttempts || !isTransientDownloadError(error)) break;
    }
  }
  throw lastError;
};

// Prefer the server's Content-Disposition filename (either header casing)
// over a caller-supplied fallback, so downloads keep whatever name/
// extension the backend actually set (e.g. Karma's .xlsx export).
export const getFileNameFromHeaders = (headers, fallback) => {
  const disposition = headers?.['content-disposition'] || headers?.['Content-Disposition'];
  if (!disposition) return fallback;

  const match = disposition.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
};

// Triggers a browser "save as" for an already-fetched blob, the same way
// the old bespoke per-feature download handlers did (an off-DOM anchor
// click), so both the shared job tray and any feature-specific download
// path (e.g. Karma's auto-download on completion) behave identically.
export const saveBlobAsFile = (blob, fileName) => {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};
